import { App } from "../interfaces"
import { menu } from "../constants"
import { useMemo } from "react"
import { hasAccessPolicy, hasModuleAccess } from "../utils"

interface IParams {
  authState: App.AuthState | null
}

// Старая логика фильтрации по иерархическим ролям (для STANDARD авторизации)
function filterByHierarchicalRole(
  menu: App.RecursiveMenuItem[],
  authState: App.AuthState | null,
): App.RecursiveMenuItem[] {
  const role = authState?.role ?? App.UserRole.ANON
  return menu.reduce<App.RecursiveMenuItem[]>((res, item) => {
    if (item.accessPolicy !== undefined) {
      if (!hasAccessPolicy(item.accessPolicy, authState)) return res
    } else if (item.role > role || (item.exactRole && item.role !== role)) {
      return res
    }
    const newItem =
      "items" in item
        ? {
            ...item,
            items: filterByHierarchicalRole(item.items, authState),
          }
        : item
    res.push(newItem)
    return res
  }, [])
}

// Новая логика фильтрации по модульным ролям (для OIDC авторизации)
function filterByModuleRoles(
  menu: App.RecursiveMenuItem[],
  authState: App.AuthState,
): App.RecursiveMenuItem[] {
  const userRoles = (authState.userInfo as App.OIDCUserInfo)?.roles || []
  return menu.reduce<App.RecursiveMenuItem[]>((res, item) => {
    if (item.accessPolicy !== undefined) {
      if (hasAccessPolicy(item.accessPolicy, authState)) {
        res.push(
          "items" in item
            ? { ...item, items: filterByModuleRoles(item.items, authState) }
            : item,
        )
      }
      return res
    }

    if ("items" in item) {
      // Это группа - сначала фильтруем дочерние элементы
      const filteredItems = filterByModuleRoles(item.items, authState)

      // Показываем группу, если:
      // 1. У неё есть дочерние элементы после фильтрации, ИЛИ
      // 2. У группы есть moduleRole и пользователь имеет доступ к модулю
      if (
        filteredItems.length > 0 ||
        (item.moduleRole && hasModuleAccess(userRoles, item.moduleRole))
      ) {
        res.push({
          ...item,
          items: filteredItems,
        })
      }
    } else {
      // Это обычный элемент меню
      // Если у элемента есть moduleRole, проверяем доступ к модулю
      if (item.moduleRole) {
        if (hasModuleAccess(userRoles, item.moduleRole)) {
          res.push(item)
        }
      }
      // Если moduleRole нет и exactRole установлен
      else if (item.exactRole) {
        // Для OIDC пропускаем элементы с exactRole без moduleRole
        // (это элементы вроде "Вход", которые должны показываться только анонимным)
        return res
      }
      // Если нет ни moduleRole, ни exactRole - это элементы общего доступа (например, "Выход")
      else {
        res.push(item)
      }
    }

    return res
  }, [])
}

// Возвращает первый url из отфильтрованного дерева меню
export function getFirstMenuUrl(items: App.RecursiveMenuItem[]): string | null {
  for (const item of items) {
    if ("items" in item) {
      const found = getFirstMenuUrl(item.items)
      if (found) return found
    } else if (item.url && item.url !== "/login" && item.url !== "/logout") {
      return item.url
    }
  }
  return null
}

// Основная функция фильтрации, выбирающая логику в зависимости от типа авторизации
export function filterMenu(
  menu: App.RecursiveMenuItem[],
  authState: App.AuthState | null,
): App.RecursiveMenuItem[] {
  // Если нет авторизации, возвращаем только элементы для анонимных пользователей
  if (!authState) {
    return filterByHierarchicalRole(menu, null)
  }

  if (authState.type === App.AuthType.STANDARD) {
    // Используем старую логику для STANDARD авторизации
    return filterByHierarchicalRole(menu, authState)
  } else if (authState.type === App.AuthType.OIDC) {
    // Используем новую логику для OIDC авторизации
    return filterByModuleRoles(menu, authState)
  }

  // Fallback: если тип авторизации неизвестен, используем иерархическую логику
  return filterByHierarchicalRole(menu, authState)
}

export const useMenu = ({ authState }: IParams): App.RecursiveMenuItem[] => {
  return useMemo(() => filterMenu(menu, authState), [authState])
}
